import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { INameId } from 'constants/globalTypes'
import { IDishesModal } from 'pages/Food/DishesModal'
import { dietaryPreferences } from 'pages/Food/foodData'
import { IIngredientsModal } from 'pages/Food/IngredientsModal'
import { IMealSupplierModalState } from 'pages/Food/MealSuppliersModal'
import { IMealTypeModal } from 'pages/Food/MealTypeModal'
import { IAddFoodItemModalState } from 'pages/Food/FoodItems/AddFoodItemModal'
import { toast } from 'react-toastify'
import {
  IGenerateFoodReportRequest,
  ICreateDishRequest,
  ICreateIngredientRequest,
  ICreateMealSupplierRequest,
  ICreateMealTypeRequest,
  ICreateUpdateFoodInventoryItemRequest,
  IDishSingleResponse,
  IFoodCategorySingleResponse,
  IFoodInventoyItemSingleResponse,
  IGetAllDishesSingleResponse,
  IGetFoodReportResponse,
  IIngredientSingleResponse,
  IMealSupplierSingleResponse,
  IFoodReport,
  IEditFoodReport,
  IGetSelectedDishesReponse,
  IPostSelectedDishRequest,
} from 'types/api/foodApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IFoodSlice } from 'types/food.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, getParams, handleErrors } from 'utils/helpers'
import { ISelectedDish } from 'pages/Food/SelectedDishes/AddSelectedDishModal'
import { clear } from 'console'

const initialState: IFoodSlice = {
  isLoading: false,
  foodReport: null,
  oldFoodReport: null,
  selectedDishes: null,
  dishes: {
    data: [],
    singleDish: null,
    totalPages: 0,
    totalCount: 0,
  },
  mealTypes: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  mealSuppliers: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  mealIngredients: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  categories: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  items: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
}

export const getFoodReport = createAsyncThunk('food/getFoodReport', async (date: any, thunkAPI) => {
  try {
    const resp: AxiosResponse<IGetFoodReportResponse> = await baseUrl.get(
      'food/management/report',
      {
        params: { date: new Date(date).toISOString() },
      },
    )

    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const generateFoodReport = createAsyncThunk(
  'food/generateFoodReport',
  async (data: IGenerateFoodReportRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodReport> = await baseUrl.post(
        'food/management/report/generate',
        data,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFoodReport = createAsyncThunk(
  'food/editFoodReport',
  async ({ report, id }: { report: IEditFoodReport; id: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse<any> = await baseUrl.patch(
        `food/management/report/${id}/edit`,
        report,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const confirmFoodReport = createAsyncThunk(
  'food/confirmFoodReport',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch(`food/management/report/${id}/confirm`)
      return null
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createMealType = createAsyncThunk(
  'food/createMealType',
  async (req: ICreateMealTypeRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<INameId> = await baseUrl.post('food/management/meal/type', req)
      await thunkAPI.dispatch(getMealTypes(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editMealType = createAsyncThunk(
  'food/editMealType',
  async (state: IMealTypeModal, thunkAPI) => {
    try {
      const { id } = state
      delete state.id
      const resp: AxiosResponse<INameId> = await baseUrl.patch('food/management/meal/type/' + id, {
        name: state.type,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteMealType = createAsyncThunk(
  'food/deleteMealType',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/meal/type/' + id)
      await thunkAPI.dispatch(getMealTypes(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createMealSupplier = createAsyncThunk(
  'food/createSupplier',
  async (state: IMealSupplierModalState, thunkAPI) => {
    try {
      const req: ICreateMealSupplierRequest = {
        ...state,
        dietaryPreferenceTypes: state.dietaryPreferenceTypes.map((i) => i.value),
      }
      const resp: AxiosResponse<IMealSupplierSingleResponse> = await baseUrl.post(
        'food/management/meal/supplier',
        req,
      )

      await thunkAPI.dispatch(getMealSuppliers(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editMealSupplier = createAsyncThunk(
  'food/editSupplier',
  async (state: IMealSupplierModalState, thunkAPI) => {
    try {
      const { id } = state
      delete state.id
      const req: ICreateMealSupplierRequest = {
        ...state,
        dietaryPreferenceTypes: state.dietaryPreferenceTypes.map((i) => i.value),
      }
      const resp: AxiosResponse<Omit<IMealSupplierSingleResponse, 'id'>> = await baseUrl.patch(
        'food/management/meal/supplier/' + id,
        req,
      )
      return { ...resp.data, id }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteMealSupplier = createAsyncThunk(
  'food/deleteMealSupplier',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/meal/supplier/' + id)

      await thunkAPI.dispatch(getMealSuppliers(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createIngredient = createAsyncThunk(
  'food/createIngredient',
  async (state: IIngredientsModal, thunkAPI) => {
    try {
      const req: ICreateIngredientRequest = {
        name: state.name,
        unitId: state.unit.value,
      }
      const resp: AxiosResponse<IIngredientSingleResponse> = await baseUrl.post(
        'food/management/ingredient',
        req,
      )

      await thunkAPI.dispatch(getMealIngredients(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editIngredient = createAsyncThunk(
  'food/editIngredient',
  async (state: IIngredientsModal, thunkAPI) => {
    try {
      const req: ICreateIngredientRequest = {
        name: state.name,
        unitId: state.unit.value,
      }
      const resp: AxiosResponse<IIngredientSingleResponse> = await baseUrl.patch(
        'food/management/ingredient/' + state.id,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteIngredient = createAsyncThunk(
  'food/deleteIngredient',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/ingredient/' + id)

      await thunkAPI.dispatch(getMealIngredients(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createDish = createAsyncThunk(
  'food/createDish',
  async (state: IDishesModal, thunkAPI) => {
    try {
      const req: ICreateDishRequest = {
        name: state.name,
        ingredients: state.ingredients.map((i) => ({
          ingredientId: i.ingredient.value,
          amount: i.amount,
        })),
        supplierId: state.supplier.value,
        mealTypeId: state.mealType.value,
        dietaryPreferenceTypes: state.dietaryPreferences.map((i) => i.value),
      }
      const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.post(
        // TODO srediti ostale response tipove
        'food/management/dish',
        req,
      )

      await thunkAPI.dispatch(getDishes(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editDish = createAsyncThunk('food/editDish', async (state: IDishesModal, thunkAPI) => {
  try {
    const req: ICreateDishRequest = {
      name: state.name,
      ingredients: state.ingredients.map((i) => ({
        ingredientId: i.ingredient.value,
        amount: i.amount,
      })),
      supplierId: state.supplier.value,
      mealTypeId: state.mealType.value,
      dietaryPreferenceTypes: state.dietaryPreferences.map((i) => i.value),
    }
    const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.patch(
      // TODO srediti ostale response tipove
      'food/management/dish/' + state.id,
      req,
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteDish = createAsyncThunk('food/deleteDish', async (id: number, thunkAPI) => {
  try {
    await baseUrl.delete('food/management/dish/' + id)

    await thunkAPI.dispatch(getDishes(getParams({ location })))
    return id
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getMealTypes = createAsyncThunk(
  'food/getAllMealTypes',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<INameId>> = await baseUrl.get(
        'food/management/meal/type',
        {
          params,
        },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDishes = createAsyncThunk('food/getAllDishes', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IGetAllDishesSingleResponse>> = await baseUrl.get(
      'food/management/dish',
      {
        params,
      },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getSingleDish = createAsyncThunk(
  'food/getSingleDish',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.get(
        'food/management/dish/' + id,
      )
      const values: IDishesModal = {
        id: resp.data.id,
        name: resp.data.name,
        mealType: resp.data.mealType
          ? { label: resp.data.mealType.name, value: resp.data.mealType.id }
          : null,
        dietaryPreferences: resp.data.dietaryPreferenceTypes.map((p) => ({
          value: p,
          label: dietaryPreferences.find((dp) => dp.value == p).label,
        })),
        ingredients:
          resp.data.ingredients?.map((i) => ({
            amount: i.amount,
            ingredient: {
              unit: i.ingredient.unit.name,
              value: i.ingredient.id,
              label: i.ingredient.name,
            },
          })) || [],
        supplier: {
          label: resp.data.supplier?.name || '',
          value: resp.data.supplier?.id || null,
          isConnectedWithFoodInventory: resp.data.supplier?.isConnectedWithFoodInventory || false,
        },
      }
      return values
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMealSuppliers = createAsyncThunk(
  'food/getAllSuppliers',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IMealSupplierSingleResponse>> =
        await baseUrl.get('food/management/meal/supplier', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMealIngredients = createAsyncThunk(
  'food/getAllIngredients',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IIngredientSingleResponse>> = await baseUrl.get(
        'food/management/ingredient',
        {
          params,
        },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodCategories = createAsyncThunk(
  'food/getAllCategories',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodCategorySingleResponse>> =
        await baseUrl.get('food/inventory/category', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodCategory = createAsyncThunk(
  'food/createCategory',
  async (state: { name: string; imageFile: File }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodCategorySingleResponse> = await baseUrl.post(
        'food/inventory/category',
        { name: state.name },
      )
      let respImg: AxiosResponse<IFoodCategorySingleResponse>
      if (state.imageFile) {
        const formData = new FormData()
        formData.append('image', state.imageFile)
        respImg = await baseUrl.post('food/inventory/category/' + resp.data.id + '/image', formData)
      }
      await thunkAPI.dispatch(getFoodCategories(getParams({ location })))

      return { ...resp.data, imageUrl: respImg?.data?.imageUrl || resp.data.imageUrl }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFoodCategory = createAsyncThunk(
  'food/editCategory',
  async (state: { name: string; id: number; imageFile: File }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodCategorySingleResponse> = await baseUrl.patch(
        'food/inventory/category/' + state.id,
        { name: state.name },
      )
      let respImg: AxiosResponse<IFoodCategorySingleResponse>

      if (state.imageFile) {
        const formData = new FormData()
        formData.append('image', state.imageFile)
        respImg = await baseUrl.post('food/inventory/category/' + state.id + '/image', formData)
      }
      return { ...resp.data, imageUrl: respImg?.data?.imageUrl || resp.data.imageUrl }
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodCategory = createAsyncThunk(
  'food/deleteCategory',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/inventory/category/' + id)

      await thunkAPI.dispatch(getFoodCategories(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodInventoryItems = createAsyncThunk(
  'food/getAllItems',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodInventoyItemSingleResponse>> =
        await baseUrl.get('food/inventory/item', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodInventoryItem = createAsyncThunk(
  'food/createItem',
  async (state: IAddFoodItemModalState, thunkAPI) => {
    try {
      const req: ICreateUpdateFoodInventoryItemRequest = {
        ingredientId: state.ingredient.value,
        categoryId: state.category.value,
        name: state.name,
        amount: state.amount,
        price: state.price,
      }
      const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.post(
        'food/inventory/item',
        req,
      )
      await thunkAPI.dispatch(getFoodInventoryItems(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFoodInventoryItem = createAsyncThunk(
  'food/editItem',
  async (state: IAddFoodItemModalState, thunkAPI) => {
    try {
      const req: ICreateUpdateFoodInventoryItemRequest = {
        ingredientId: state.ingredient.value,
        categoryId: state.category.value,
        name: state.name,
        amount: state.amount,
        price: state.price,
      }
      const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.patch(
        'food/inventory/item/' + state.id,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodInventoryItem = createAsyncThunk(
  'food/deleteItem',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/inventory/item/' + id)

      await thunkAPI.dispatch(getFoodInventoryItems(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSelectedDishes = createAsyncThunk('food/getSelectedDishes', async (_, thunkAPI) => {
  try {
    const resp: AxiosResponse<IGetSelectedDishesReponse> = await baseUrl.get(
      'food/management/company/selected-dishes',
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const addSelectedDish = createAsyncThunk(
  'food/addSelectedDish',
  async (dish: ISelectedDish[], thunkAPI) => {
    try {
      const req: IPostSelectedDishRequest = {
        selectedFoodDishes: dish.map((d) => ({
          dayInWeek: d.day.value,
          dietaryPreferenceType: d.dietaryPreference.value,
          mealTypeId: d.mealType.value,
          dishId: d.dish.value,
          supplierId: d.supplier.value,
        })),
      }
      await baseUrl.post('food/management/company/selected-dishes', req)

      await thunkAPI.dispatch(getSelectedDishes())
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const foodSlice = createSlice({
  name: 'food',
  initialState,
  reducers: {
    clearSingleDish: (state) => {
      state.dishes.singleDish = null
    },
    clearFood: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state = initialState
    },
    clearFoodReport: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state.foodReport = null
    },
    clearFoodCategories: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state.categories.data = []
    },
    clearFoodInventoryItems: (state) => {
      state.categories.data = []
    },
    clearSelectedDishes: (state) => {
      state.selectedDishes = null
    },
  },
  extraReducers: {
    [createMealType.pending.type]: (state) => {
      state.isLoading = true
    },
    [createMealType.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Meal type created successfully')
    },
    [createMealType.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSelectedDishes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSelectedDishes.fulfilled.type]: (
      state,
      { payload }: { payload: IGetSelectedDishesReponse },
    ) => {
      state.isLoading = false
      state.selectedDishes = payload
    },
    [getSelectedDishes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addSelectedDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [addSelectedDish.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Dish added successfully')
    },
    [addSelectedDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodReport.fulfilled.type]: (state, { payload }: { payload: IGetFoodReportResponse }) => {
      state.isLoading = false
      state.oldFoodReport = payload.report
    },
    [getFoodReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [confirmFoodReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [confirmFoodReport.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food report confirmed successfully')
    },
    [confirmFoodReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [generateFoodReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [generateFoodReport.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.foodReport = payload
      toast.success('Food report generated successfully')
    },
    [generateFoodReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editFoodReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [editFoodReport.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food report edited generated')
    },
    [editFoodReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editMealType.pending.type]: (state) => {
      state.isLoading = true
    },
    [editMealType.fulfilled.type]: (state, { payload }: { payload: INameId }) => {
      state.isLoading = false
      toast.success('Meal type updated successfully')
      state.mealTypes.data = state.mealTypes.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
    },
    [editMealType.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteMealType.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteMealType.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Meal type deleted successfully')
    },
    [deleteMealType.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createMealSupplier.pending.type]: (state) => {
      state.isLoading = true
    },
    [createMealSupplier.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Meal supplier created successfully')
    },
    [createMealSupplier.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editMealSupplier.pending.type]: (state) => {
      state.isLoading = true
    },
    [editMealSupplier.fulfilled.type]: (
      state,
      { payload }: { payload: IMealSupplierSingleResponse },
    ) => {
      state.isLoading = false
      toast.success('Meal supplier updated successfully')
      state.mealSuppliers.data = state.mealSuppliers.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
    },
    [editMealSupplier.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteMealSupplier.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteMealSupplier.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Meal supplier deleted successfully')
    },
    [deleteMealSupplier.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createIngredient.pending.type]: (state) => {
      state.isLoading = true
    },
    [createIngredient.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Ingredient created successfully')
    },
    [createIngredient.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editIngredient.pending.type]: (state) => {
      state.isLoading = true
    },
    [editIngredient.fulfilled.type]: (
      state,
      { payload }: { payload: IIngredientSingleResponse },
    ) => {
      state.isLoading = false
      toast.success('Ingredient updated successfully')
      state.mealIngredients.data = state.mealIngredients.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
    },
    [editIngredient.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteIngredient.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteIngredient.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Ingredient deleted successfully')
    },
    [deleteIngredient.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [createDish.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Dish created successfully')
    },
    [createDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [editDish.fulfilled.type]: (state, { payload }: { payload: IDishSingleResponse }) => {
      state.isLoading = false
      toast.success('Dish updated successfully')
      state.dishes.data = state.dishes.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
    },
    [editDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteDish.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Dish deleted successfully')
    },
    [deleteDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDishes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDishes.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllDishesSingleResponse> },
    ) => {
      state.isLoading = false
      state.dishes.data = payload.data
      state.dishes.totalCount = payload.totalCount
      state.dishes.totalPages = payload.totalPages
    },
    [getDishes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleDish.fulfilled.type]: (state, { payload }: { payload: IDishesModal }) => {
      state.isLoading = false
      state.dishes.singleDish = payload
    },
    [getSingleDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getMealTypes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getMealTypes.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<INameId> },
    ) => {
      state.isLoading = false
      state.mealTypes.data = payload.data
      state.mealTypes.totalCount = payload.totalCount
      state.mealTypes.totalPages = payload.totalPages
    },
    [getMealTypes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getMealSuppliers.pending.type]: (state) => {
      state.isLoading = true
    },
    [getMealSuppliers.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IMealSupplierSingleResponse> },
    ) => {
      state.isLoading = false
      state.mealSuppliers.data = payload.data
      state.mealSuppliers.totalCount = payload.totalCount
      state.mealSuppliers.totalPages = payload.totalPages
    },
    [getMealSuppliers.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getMealIngredients.pending.type]: (state) => {
      state.isLoading = true
    },
    [getMealIngredients.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IIngredientSingleResponse> },
    ) => {
      state.isLoading = false
      state.mealIngredients.data = payload.data
      state.mealIngredients.totalCount = payload.totalCount
      state.mealIngredients.totalPages = payload.totalPages
    },
    [getMealIngredients.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodCategories.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodCategories.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodCategorySingleResponse> },
    ) => {
      state.isLoading = false
      state.categories.data = payload.data
      state.categories.totalCount = payload.totalCount
      state.categories.totalPages = payload.totalPages
    },
    [getFoodCategories.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [createFoodCategory.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food category updated successfully')
    },
    [createFoodCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editFoodCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [editFoodCategory.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodCategorySingleResponse },
    ) => {
      state.isLoading = false
      state.categories.data = state.categories.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
      toast.success('Food category updated successfully')
    },
    [editFoodCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteFoodCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteFoodCategory.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food category deleted successfully')
    },
    [deleteFoodCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodInventoryItems.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodInventoryItems.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodInventoyItemSingleResponse> },
    ) => {
      state.isLoading = false
      state.items.data = payload.data
      state.items.totalCount = payload.totalCount
      state.items.totalPages = payload.totalPages
    },
    [getFoodInventoryItems.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteFoodInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteFoodInventoryItem.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.isLoading = false
      state.items.data = state.items.data.filter((d) => d.id !== payload)
      toast.success('Food item deleted successfully')
    },
    [deleteFoodInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [createFoodInventoryItem.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food item created successfully')
    },
    [createFoodInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editFoodInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [editFoodInventoryItem.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodInventoyItemSingleResponse },
    ) => {
      state.isLoading = false
      state.items.data = state.items.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
      toast.success('Food item updated successfully')
    },
    [editFoodInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const {
  clearFood,
  clearSingleDish,
  clearFoodCategories,
  clearFoodInventoryItems,
  clearFoodReport,
  clearSelectedDishes,
} = foodSlice.actions
export default foodSlice.reducer
